import React, { useState, useRef, useEffect, useMemo, useLayoutEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { FiChevronDown, FiSearch, FiCheck, FiX } from 'react-icons/fi';
import './SearchableSelect.scss';

const POP_EST_HEIGHT = 300;

/**
 * Searchable combobox — a drop-in replacement for a native <select>.
 * The dropdown renders in a portal so it is never clipped by overflow:hidden
 * ancestors (e.g. section cards).
 *
 * options: Array<{ value, label, sublabel?, color? }>
 * value:   the currently-selected option value (compared as string)
 * onChange(value): called with the chosen option's value
 */
function SearchableSelect({
  value,
  onChange,
  options = [],
  placeholder,
  searchPlaceholder,
  emptyText,
  disabled,
  id,
  required,
  clearable = false,
}) {
  const { t } = useTranslation();
  const wrapRef = useRef(null);
  const popRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIdx, setActiveIdx] = useState(0);
  const [pos, setPos] = useState(null);

  const selected = useMemo(
    () => options.find((o) => String(o.value) === String(value)) || null,
    [options, value]
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) =>
      (o.label || '').toLowerCase().includes(q)
      || (o.sublabel || '').toLowerCase().includes(q)
      || String(o.value).toLowerCase().includes(q)
    );
  }, [options, query]);

  const updatePosition = useCallback(() => {
    const el = wrapRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const gap = 6;
    const popH = popRef.current?.offsetHeight || POP_EST_HEIGHT;
    const spaceBelow = window.innerHeight - r.bottom;
    const up = spaceBelow < popH + gap && r.top > popH + gap;
    const top = up ? Math.max(8, r.top - popH - gap) : r.bottom + gap;
    setPos({ left: r.left, top, width: r.width });
  }, []);

  // On open: reset the query, focus the search box, seed active index.
  useEffect(() => {
    if (!open) return undefined;
    setQuery('');
    const selIdx = options.findIndex((o) => String(o.value) === String(value));
    setActiveIdx(selIdx >= 0 ? selIdx : 0);
    const tid = setTimeout(() => inputRef.current?.focus(), 0);
    return () => clearTimeout(tid);
  }, [open]);

  // Position before paint, and keep in sync while open (also as the list resizes).
  useLayoutEffect(() => { if (open) updatePosition(); }, [open, filtered.length, updatePosition]);
  useEffect(() => {
    if (!open) return undefined;
    const handler = () => updatePosition();
    window.addEventListener('scroll', handler, true);
    window.addEventListener('resize', handler);
    return () => { window.removeEventListener('scroll', handler, true); window.removeEventListener('resize', handler); };
  }, [open, updatePosition]);

  // Close on outside click (accounting for the portaled popover).
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (wrapRef.current?.contains(e.target)) return;
      if (popRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  // Keep the active option scrolled into view.
  useEffect(() => {
    if (!open || !listRef.current) return;
    const el = listRef.current.querySelector('.ssel__option.is-active');
    if (el) el.scrollIntoView({ block: 'nearest' });
  }, [activeIdx, open]);

  const choose = (opt) => { onChange(opt.value); setOpen(false); };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx((i) => Math.min(i + 1, filtered.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIdx((i) => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); if (filtered[activeIdx]) choose(filtered[activeIdx]); }
    else if (e.key === 'Escape') { e.preventDefault(); setOpen(false); }
  };

  return (
    <div className={`ssel${disabled ? ' ssel--disabled' : ''}`} ref={wrapRef}>
      <button
        type="button"
        id={id}
        className={`ssel__trigger${open ? ' is-open' : ''}${!selected ? ' is-placeholder' : ''}`}
        onClick={() => !disabled && setOpen((o) => !o)}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        data-required={required || undefined}
      >
        {selected?.color && <span className="ssel__dot" style={{ background: selected.color }} />}
        <span className="ssel__label">
          {selected ? selected.label : (placeholder || t('common.selectOption', { defaultValue: 'Select…' }))}
        </span>
        {selected?.sublabel && <span className="ssel__sublabel">{selected.sublabel}</span>}
        {clearable && selected && !disabled && (
          <span
            className="ssel__clear"
            role="button"
            tabIndex={-1}
            aria-label={t('common.clear', { defaultValue: 'Clear' })}
            onClick={(e) => { e.stopPropagation(); onChange(''); }}
          >
            <FiX />
          </span>
        )}
        <FiChevronDown className="ssel__chevron" />
      </button>

      {open && createPortal(
        <div
          className="ssel__pop"
          role="listbox"
          ref={popRef}
          style={{ left: pos?.left ?? -9999, top: pos?.top ?? -9999, width: pos?.width }}
        >
          <div className="ssel__search">
            <FiSearch className="ssel__search-icon" />
            <input
              ref={inputRef}
              className="ssel__search-input"
              value={query}
              onChange={(e) => { setQuery(e.target.value); setActiveIdx(0); }}
              onKeyDown={onKeyDown}
              placeholder={searchPlaceholder || t('common.search', { defaultValue: 'Search…' })}
            />
          </div>

          <div className="ssel__list" ref={listRef}>
            {filtered.length === 0 ? (
              <div className="ssel__empty">{emptyText || t('common.noResults', { defaultValue: 'No results' })}</div>
            ) : (
              filtered.map((opt, i) => {
                const isSel = String(opt.value) === String(value);
                return (
                  <div
                    key={opt.value ?? i}
                    className={`ssel__option${i === activeIdx ? ' is-active' : ''}${isSel ? ' is-selected' : ''}`}
                    onMouseEnter={() => setActiveIdx(i)}
                    onClick={() => choose(opt)}
                    role="option"
                    aria-selected={isSel}
                  >
                    {opt.color && <span className="ssel__dot" style={{ background: opt.color }} />}
                    <span className="ssel__option-label">{opt.label}</span>
                    {opt.sublabel && <span className="ssel__option-sub">{opt.sublabel}</span>}
                    {isSel && <FiCheck className="ssel__option-check" />}
                  </div>
                );
              })
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

export default SearchableSelect;
